import { create } from 'zustand'
import type { DeploymentJob } from '../services/api'

/** Where a tenant's dismissed task ids are kept in this browser. */
export const dismissedStorageKey = (tenantId: string | null) =>
  `taskList.dismissed.${tenantId ?? 'none'}`

const readDismissed = (tenantId: string | null): Set<string> => {
  try {
    const raw = window.localStorage.getItem(dismissedStorageKey(tenantId))
    const ids = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(ids) ? ids.filter((x) => typeof x === 'string') : [])
  } catch {
    // Private-browsing and storage-disabled profiles throw on read; a dismissal then lasts for
    // this page view only, which beats a header that fails to render.
    return new Set()
  }
}

const writeDismissed = (tenantId: string | null, ids: Set<string>) => {
  try {
    window.localStorage.setItem(dismissedStorageKey(tenantId), JSON.stringify([...ids]))
  } catch {
    /* remembered for this page view only */
  }
}

interface TaskListStore {
  open: boolean
  openTaskList: () => void
  closeTaskList: () => void
  toggleTaskList: () => void
  /** The tenant whose dismissed set is loaded. Dismissals are per tenant: an MSP operator who
   *  dismissed a run in one tenant has not read the same-numbered run in another. */
  tenantId: string | null
  dismissed: ReadonlySet<string>
  loadDismissed: (tenantId: string | null) => void
  dismiss: (id: string) => void
  dismissMany: (ids: Iterable<string>) => void
  /** Drop every dismissed id not in `keep`, so the set cannot outgrow what the lists can hold. */
  prune: (keep: Iterable<string>) => void

  /** Jobs this browser just started, so the list shows them before its next poll rather than up
   *  to half a minute later. A counter per kind: the list refetches when it moves. */
  deploymentStartSeq: number
  /** The run that was just recorded, in the shape the list reads, so it can be shown at once. */
  startedDeployment: DeploymentJob | null
  noteDeploymentStarted: (job: DeploymentJob) => void
  packagingStartSeq: number
  notePackagingStarted: () => void
}

/**
 * The header task list: whether it is open, and which finished tasks have been read.
 *
 * A store rather than component state because the thing that opens it is usually on its way out —
 * the deploy wizard closes as it hands its job to a worker, and the banner it leaves behind offers
 * the list — and because the deployments page and the header read the same dismissals.
 */
export const useTaskListStore = create<TaskListStore>((set, get) => ({
  open: false,
  openTaskList: () => set({ open: true }),
  closeTaskList: () => set({ open: false }),
  toggleTaskList: () => set((s) => ({ open: !s.open })),

  tenantId: null,
  dismissed: new Set<string>(),
  loadDismissed: (tenantId) => set({ tenantId, dismissed: readDismissed(tenantId) }),
  dismiss: (id) => get().dismissMany([id]),
  dismissMany: (ids) => {
    const next = new Set(get().dismissed)
    for (const id of ids) next.add(id)
    writeDismissed(get().tenantId, next)
    set({ dismissed: next })
  },
  prune: (keep) => {
    const keepSet = new Set(keep)
    const current = get().dismissed
    const next = new Set([...current].filter((id) => keepSet.has(id)))
    if (next.size === current.size) return
    writeDismissed(get().tenantId, next)
    set({ dismissed: next })
  },

  deploymentStartSeq: 0,
  startedDeployment: null,
  noteDeploymentStarted: (job) =>
    set((s) => ({ deploymentStartSeq: s.deploymentStartSeq + 1, startedDeployment: job })),
  packagingStartSeq: 0,
  notePackagingStarted: () => set((s) => ({ packagingStartSeq: s.packagingStartSeq + 1 })),
}))
